import React from "react";
import { PaymentPlan } from "../config";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

interface PricingPlan {
  name: string;
  credits: string;
  price: string;
  features: string[];
  planName: string;
}

interface PricingOverlayProps {
  isOpen: boolean;
  remainingCredits: number;
  onClose: () => void;
  onCreditPurchase: (plan: keyof typeof PaymentPlan) => Promise<void>;
}

const pricingPlans: PricingPlan[] = [
  {
    name: "Basic",
    credits: "400",
    price: "20",
    features: [
      "Unlimited Keyword Alerting",
      "Meeting Notes, Summaries etc.",
      "AI Copilot",
      "24/7 Support",
      "Lifetime Updates",
      "Feature Requests",
    ],
    planName: "basic",
  },
  {
    name: "Pro",
    credits: "10000",
    price: "100",
    features: [
      "Unlimited Keyword Alerting",
      "Meeting Notes, Summaries etc.",
      "AI Copilot",
      "24/7 Support",
      "Lifetime Updates",
      "Feature Requests",
    ],
    planName: "premium",
  },
];

const CrossIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 6L9 17L4 12"
      stroke="#3BA1B8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const responsive = {
  mobile: {
    breakpoint: { max: 1200, min: 0 },
    items: 1,
  },
};

const PricingCard: React.FC<{ plan: PricingPlan; onCreditPurchase: any }> = ({
  plan,
  onCreditPurchase,
}) => (
  <div style={styles.pricingCard}>
    <h4 style={styles.cardTitle}>{plan.name} Pack</h4>
    <div style={styles.credits}>{plan.credits} Minutes</div>
    <div style={styles.price}>${plan.price}</div>
    <ul style={styles.featureList}>
      {plan.features.map((feature, index) => (
        <li key={index} style={styles.featureItem}>
          <CheckIcon />
          <span style={styles.featureText}>{feature}</span>
        </li>
      ))}
    </ul>
    <button
      style={styles.buyButton}
      onClick={() => onCreditPurchase(plan.planName)}
    >
      Buy Now
    </button>
  </div>
);

// Update the custom CSS for the carousel
const customStyles = `
  .custom-dot-list-style {
    position: absolute;
    bottom: 20px;
    display: flex !important;
    justify-content: center;
    width: 100%;
  }
  .custom-dot-list-style li {
    margin: 0 5px;
  }
  .custom-dot-list-style button {
    border: none;
    background: rgba(255, 255, 255, 0.5);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    padding: 0;
  }
  .custom-dot-list-style .react-multi-carousel-dot--active button {
    background: white;
  }
  .carousel-container {
    width: 100%;
    padding-bottom: 40px;
    justify-content: center;
  }
  .carousel-item {
    padding: 0 20px;
  }
  /* Force single slide view */
  .react-multi-carousel-track {
    margin: 0 !important;
    transform: none !important;
  }
  .react-multi-carousel-item {
    width: 100% !important;
    position: relative !important;
    left: 0 !important;
    transform: none !important;
    flex: 0 0 100%;
    display: none;
  }
  .react-multi-carousel-item--active {
    display: block;
  }
`;

// Add the styles to the document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = customStyles;
document.head.appendChild(styleSheet);

export const PricingOverlay: React.FC<PricingOverlayProps> = ({
  isOpen,
  remainingCredits,
  onClose,
  onCreditPurchase,
}) => {
  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.content}>
        <button style={styles.closeButton} onClick={onClose}>
          <CrossIcon />
        </button>
        {remainingCredits <= 0 && (
          <p style={{ fontSize: 16, paddingBottom: 16, textAlign: "center" }}>
            You have run out of minutes. Please purchase more to continue using
            NameAlert.
          </p>
        )}
        <Carousel
          responsive={responsive}
          infinite={true}
          showDots={true}
          removeArrowOnDeviceType={["mobile"]}
          dotListClass="custom-dot-list-style"
          containerClass="carousel-container"
          itemClass="carousel-item"
          partialVisible={false}
          centerMode={false}
          swipeable={true}
          ssr={true}
          arrows={false}
          autoPlay={false}
          draggable={true}
          minimumTouchDrag={80}
          slidesToSlide={1}
        >
          {pricingPlans.map((plan, index) => (
            <PricingCard
              key={index}
              plan={plan}
              onCreditPurchase={onCreditPurchase}
            />
          ))}
        </Carousel>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.25)",
    backdropFilter: "blur(4px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  content: {
    backgroundColor: "#ffc45e",
    padding: "1.5rem",
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  closeButton: {
    position: "absolute",
    top: "calc(2rem + env(safe-area-inset-top))",
    right: "1rem",
    boxShadow: "none",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#FFFFFF",
    padding: "8px",
    zIndex: 1,
  },
  pricingCard: {
    width: "100%",
    margin: "0",
    backgroundColor: "white",
    borderRadius: "10px",
    padding: "1.5rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    boxSizing: "border-box",
  },
  cardTitle: {
    fontSize: "14px",
    marginBottom: "0.25rem",
    fontWeight: "normal",
    marginTop: "0px",
  },
  credits: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#04ced9",
    marginBottom: "0.5rem",
  },
  price: {
    fontSize: "18px",
    fontWeight: "normal",
    marginBottom: "0.5rem",
  },
  featureList: {
    listStyleType: "none",
    padding: 0,
    marginBottom: "1.5rem",
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: "0.5rem",
  },
  featureText: {
    marginLeft: "0.5rem",
    fontSize: "14px",
    fontWeight: "normal",
  },
  buyButton: {
    width: "100%",
    padding: "0.75rem",
    backgroundColor: "#ffc45e",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "normal",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
};

export default PricingOverlay;
